/**
 * PDF Export Utility
 * Generates PDF files from transcript data
 */

const escapePdfText = (text) =>
  text
    .replace(/\\/g, "\\\\")
    .replace(/\(/g, "\\(")
    .replace(/\)/g, "\\)")
    .replace(/\n/g, " ");

const wrapText = (text, maxChars = 90) => {
  const words = text.split(/\s+/);
  const linesArray = [];
  let currentLine = "";

  words.forEach((word) => {
    if (!currentLine.length) {
      currentLine = word;
      return;
    }

    if ((currentLine + " " + word).length <= maxChars) {
      currentLine += ` ${word}`;
    } else {
      linesArray.push(currentLine);
      currentLine = word;
    }
  });

  if (currentLine.length) {
    linesArray.push(currentLine);
  }

  return linesArray.length ? linesArray : [text.slice(0, maxChars)];
};

/**
 * Creates a PDF blob from transcript entries
 * @param {Array} entries - Array of transcript entries with speaker and text
 * @returns {Blob} PDF blob
 */
export const createPdfBlobFromTranscript = (entries) => {
  // Process entries into lines
  const baseLines = entries.flatMap((entry) => {
    const speaker = entry?.speaker || "Speaker";
    const text = entry?.text ? entry.text.replace(/\s+/g, " ").trim() : "";
    const combined = `${speaker}: ${text}`.trim();
    if (!combined.length) {
      return [];
    }
    return wrapText(combined);
  });

  const lines = baseLines.length ? baseLines : ["Transcript is empty."];

  // PDF page dimensions: 612 x 792 points (US Letter)
  const pageWidth = 612;
  const pageHeight = 792;
  const marginTop = 72;
  const marginBottom = 72;
  const marginLeft = 72;
  const lineHeight = 18;
  const fontSize = 12;

  // Calculate how many lines fit per page
  const usableHeight = pageHeight - marginTop - marginBottom;
  const linesPerPage = Math.floor(usableHeight / lineHeight);

  // Split lines into pages
  const pages = [];
  for (let i = 0; i < lines.length; i += linesPerPage) {
    const pageLines = lines.slice(i, i + linesPerPage);
    if (pageLines.length > 0) {
      pages.push(pageLines);
    }
  }

  // Ensure at least one page
  if (pages.length === 0) {
    pages.push(["Transcript is empty."]);
  }

  // Build PDF objects
  // Object structure:
  // 1: Catalog
  // 2: Pages
  // 3, 5, 7, ...: Page objects (one per page)
  // 4, 6, 8, ...: Content streams (one per page)
  // Last: Font object

  const objects = [];
  
  // Catalog object (1)
  objects.push(`1 0 obj\n<< /Type /Catalog /Pages 2 0 R >>\nendobj\n`);

  // Pages object (2) - build kids array
  const pageObjStart = 3;
  const pageObjectNumbers = [];
  for (let i = 0; i < pages.length; i++) {
    pageObjectNumbers.push(pageObjStart + i * 2);
  }
  const kidsRefs = pageObjectNumbers.map((num) => `${num} 0 R`).join(" ");
  objects.push(
    `2 0 obj\n<< /Type /Pages /Kids [${kidsRefs}] /Count ${pages.length} >>\nendobj\n`
  );

  // Calculate font object number (after all page and content objects)
  const fontObjNum = pageObjStart + pages.length * 2;

  // Page objects and content streams
  pages.forEach((pageLines, pageIndex) => {
    const pageObjNum = pageObjStart + pageIndex * 2;
    const contentObjNum = pageObjStart + pageIndex * 2 + 1;

    // Build content stream for this page
    const startY = pageHeight - marginTop;
    const contentLines = pageLines
      .map((line, lineIndex) => {
        const y = startY - lineIndex * lineHeight;
        const escapedLine = escapePdfText(line);
        return `1 0 0 1 ${marginLeft} ${y} Tm (${escapedLine}) Tj`;
      })
      .join("\n");

    const stream = `BT\n/F1 ${fontSize} Tf\n${contentLines}\nET`;

    // Page object
    objects.push(
      `${pageObjNum} 0 obj\n<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pageWidth} ${pageHeight}] /Resources << /Font << /F1 ${fontObjNum} 0 R >> >> /Contents ${contentObjNum} 0 R >>\nendobj\n`
    );

    // Content stream object
    objects.push(
      `${contentObjNum} 0 obj\n<< /Length ${stream.length} >>\nstream\n${stream}\nendstream\nendobj\n`
    );
  });

  // Font object (last)
  objects.push(
    `${fontObjNum} 0 obj\n<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>\nendobj\n`
  );

  // Normalize objects (ensure they end with newline)
  const normalizedObjects = objects.map((obj) =>
    obj.endsWith("\n") ? obj : `${obj}\n`
  );

  // Build PDF
  let pdf = "%PDF-1.4\n";
  const offsets = [0];
  let currentOffset = pdf.length;

  normalizedObjects.forEach((obj) => {
    offsets.push(currentOffset);
    pdf += obj;
    currentOffset = pdf.length;
  });

  // XREF table
  const xrefOffset = pdf.length;
  pdf += `xref\n0 ${normalizedObjects.length + 1}\n`;
  pdf += `0000000000 65535 f \n`;
  for (let i = 1; i <= normalizedObjects.length; i++) {
    pdf += `${offsets[i].toString().padStart(10, "0")} 00000 n \n`;
  }

  // Trailer
  pdf += `trailer\n<< /Size ${normalizedObjects.length + 1} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`;

  return new Blob([pdf], { type: "application/pdf" });
};

